/**
 * Le Grand Quiz du Lair : page cachée (lien #grand-quiz-…) réservée à l'animateur.
 * Un candidat choisit 5 épreuves parmi 15, avec 3 jokers (Double Dose, Roulette, Radar)
 * et des boosts sur les épreuves boudées. Classement général enregistré en base.
 * Fenêtre « régie » (même lien + -regie) : antisèche privée synchronisée par BroadcastChannel.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BY_ID, FORMATS, HOSTS, media } from './quizShowFormats';
import { FormatLogo, makeApi, sfx } from './QuizShowParts';
import { ROUNDS } from './QuizShowRounds';
import QuizShowEditor from './QuizShowEditor';
import { ShowLogo } from './QuizShowLogos';
import { AnyAvatar, AvatarModal, Portrait, randomAvatar } from './QuizShowAvatars';
import './QuizShow.css';

export const QS_HASH = '#grand-quiz-k7v2q9';
export const SHOW_NAME = ['Le Grand Quiz', 'du Lair'];
const ROUNDS_PER_GAME = 5;
const KEY_STORE = 'qs-host-key';
const GAME_STORE = 'qs-live-game';

const read = (k) => { try { return window.localStorage.getItem(k); } catch (e) { return null; } };
const write = (k, v) => { try { if (v == null) window.localStorage.removeItem(k); else window.localStorage.setItem(k, v); } catch (e) { /* stockage indisponible */ } };
const channel = () => { try { return new BroadcastChannel('qs-regie'); } catch (e) { return null; } };
const fmtPts = (n) => (Math.round(n * 10) / 10).toString().replace('.', ',');

// Avatar : un personnage du roster (comme dans le quiz du site) ou, pour les anciennes parties, une image
function Avatar({ src, name, size = 56 }) {
  return <AnyAvatar v={src} name={name} size={size} mediaUrl={media} />;
}

function ShowTitle({ big }) {
  return <h1 className={`qs-title${big ? ' is-big' : ''}`}><ShowLogo size={big ? 520 : 200} /><span className="qs-sr">{SHOW_NAME.join(' ')}</span></h1>;
}

// Décor de plateau télé : projecteurs, sol quadrillé, paillettes
function StageBg() {
  return (
    <div className="qs-bg" aria-hidden="true">
      <div className="qs-bg-blob a" /><div className="qs-bg-blob b" /><div className="qs-bg-blob c" />
      <div className="qs-beam l" /><div className="qs-beam r" />
      <div className="qs-floor" />
      <div className="qs-sparkles">{Array.from({ length: 26 }, (_, k) => <i key={k} style={{ '--x': `${(k * 37) % 100}%`, '--y': `${(k * 53) % 100}%`, '--d': `${(k % 9) * 0.7}s`, '--s': 0.5 + ((k * 7) % 10) / 10 }} />)}</div>
    </div>
  );
}

// ——— Régie : la fenêtre privée de l'animateur ———
function Regie() {
  const [s, setS] = useState({ lines: [], title: 'En attente de la page du quiz…' });
  useEffect(() => {
    const bc = channel(); if (!bc) return undefined;
    bc.onmessage = (e) => setS(e.data || {});
    bc.postMessage({ ask: true });
    return () => bc.close();
  }, []);
  return (
    <div className="qs-regie">
      <header><ShowTitle /><span>Régie · ne partage pas cette fenêtre</span></header>
      <h2>{s.title}</h2>
      {s.player && <p className="qs-regie-player">{s.player} · {s.total ?? 0} pts</p>}
      <ul>{(s.lines || []).filter(Boolean).map((l, k) => <li key={k}>{l}</li>)}</ul>
    </div>
  );
}

// ——— Accès : clé de l'animateur ———
function Gate({ onKey }) {
  const [v, setV] = useState('');
  const [err, setErr] = useState('');
  const go = async (e) => {
    e.preventDefault(); setErr('');
    try { await makeApi(v.trim()).get('/check'); write(KEY_STORE, v.trim()); onKey(v.trim()); } catch (x) { setErr(x.message); }
  };
  return (
    <div className="qs-gate">
      <ShowTitle big />
      <form onSubmit={go}>
        <input type="password" value={v} onChange={(e) => setV(e.target.value)} placeholder="Clé de l’animateur" autoFocus />
        <button type="submit" className="qs-btn is-gold">Entrer</button>
      </form>
      {err && <p className="qs-err">{err}</p>}
    </div>
  );
}

function Lobby({ api, onStart, onResume, onEditor, onRanking, live, onCloseGame }) {
  const [player, setPlayer] = useState('');
  const [avatar, setAvatar] = useState(randomAvatar);
  const [picking, setPicking] = useState(false);
  const [err, setErr] = useState('');
  const start = async (e) => {
    e.preventDefault(); setErr('');
    try { const { game } = await api.post('/games', { player, avatar }); onStart(game); } catch (x) { setErr(x.message); }
  };
  return (
    <div className="qs-lobby">
      <ShowTitle big />
      <form className="qs-card qs-newgame" onSubmit={start}>
        <h3>Nouveau candidat</h3>
        <div className="qs-newgame-who">
          <button type="button" className="qs-av-pick" onClick={() => setPicking(true)} title="Changer de personnage"><Avatar src={avatar} name={player} size={84} /><em className="qs-av-change">Changer</em></button>
          <input value={player} onChange={(e) => setPlayer(e.target.value)} placeholder="Nom du candidat" />
        </div>
        {picking && <AvatarModal value={avatar} onPick={setAvatar} onClose={() => setPicking(false)} />}
        <button type="submit" className="qs-btn is-gold is-big">Lancer la partie</button>
        {err && <p className="qs-err">{err}</p>}
      </form>
      {live.length > 0 && (
        <div className="qs-card">
          <h3>Parties en cours</h3>
          {live.map((g) => (
            <div key={g._id} className="qs-liverow">
              <Avatar src={g.avatar} name={g.player} size={34} /><b>{g.player}</b><span>{g.rounds.length}/5 épreuves · {g.total} pts</span>
              <button type="button" className="qs-btn is-small" onClick={() => onResume(g)}>Reprendre</button>
              <button type="button" className="qs-btn is-small is-ghost" title="Fermer sans l’ajouter au classement" onClick={() => onCloseGame(g)}>✕ Fermer</button>
            </div>
          ))}
        </div>
      )}
      <div className="qs-lobby-actions">
        <button type="button" className="qs-btn" onClick={onRanking}>🏆 Classement</button>
        <button type="button" className="qs-btn" onClick={onEditor}>✏️ Éditeur des épreuves</button>
        <button type="button" className="qs-btn is-ghost" onClick={() => window.open(`${window.location.pathname}${QS_HASH}-regie`, 'qs-regie', 'width=520,height=720')}>🎧 Ouvrir la régie</button>
      </div>
    </div>
  );
}

function Ranking({ api, onBack, highlight }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => { setRows((await api.get('/games')).leaderboard); }, [api]);
  useEffect(() => { load().catch(() => setRows([])); }, [load]);
  const remove = async (g) => { if (!window.confirm(`Retirer ${g.player} du classement ?`)) return; await api.del(`/games/${g._id}`); load(); };
  return (
    <div className="qs-ranking">
      <header className="qs-subtop"><button type="button" className="qs-btn is-ghost" onClick={onBack}>← Retour</button><ShowTitle /></header>
      <h2 className="qs-section">Classement général</h2>
      {!rows ? <p className="qs-muted">Chargement…</p> : !rows.length ? <p className="qs-muted">Aucune partie terminée pour l’instant.</p> : (
        <ol className="qs-podium">
          {rows.map((g, k) => (
            <li key={g._id} className={`${k < 3 ? `is-top${k + 1}` : ''}${g._id === highlight ? ' is-me' : ''}`}>
              <span className="qs-rank">{k + 1}</span>
              <Avatar src={g.avatar} name={g.player} size={44} />
              <div className="qs-rk-name"><b>{g.player}</b><small>{g.finishedAt ? new Date(g.finishedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</small></div>
              <div className="qs-rk-rounds">{g.rounds.map((r, j) => <span key={j} title={`${BY_ID[r.format]?.name.join(' ')} · ${r.points} pts`} style={{ '--c1': BY_ID[r.format]?.c1 }}>{BY_ID[r.format]?.icon}<small>{r.points}</small></span>)}</div>
              <b className="qs-rk-total">{g.total}</b>
              <button type="button" className="qs-rk-del" title="Retirer" onClick={() => remove(g)}>✕</button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Board({ game, board, onPick, onRoulette, onRadar, armed, setArmed, spin }) {
  const played = new Set(game.rounds.map((r) => r.format));
  const total = game.rounds.reduce((s, r) => s + r.points, 0);
  return (
    <div className="qs-boardwrap">
      <div className="qs-boardtop">
        <Avatar src={game.avatar} name={game.player} />
        <div className="qs-boardtop-name"><small>Candidat</small><b>{game.player}</b></div>
        <h2>Choix de l’épreuve</h2>
        <div className="qs-dots"><small>Épreuves jouées</small><div>{Array.from({ length: ROUNDS_PER_GAME }, (_, k) => <i key={k} className={k < game.rounds.length ? 'is-on' : ''} style={k < game.rounds.length ? { '--c1': BY_ID[game.rounds[k].format].c1 } : null} />)}</div></div>
        <div className="qs-score"><b>{total}</b><small>pts</small></div>
      </div>
      <div className={`qs-board${armed === 'radar' ? ' is-radar' : ''}`}>
        {FORMATS.map((f, i) => {
          const b = board[f.id] || {};
          const off = played.has(f.id) || !b.set;
          const res = game.rounds.find((r) => r.format === f.id);
          return (
            <button type="button" key={f.id} className={`qs-cell${off ? ' is-off' : ''}${res ? ' is-played' : ''}${spin === f.id ? ' is-spin' : ''}`} disabled={off && !res} onClick={() => !off && onPick(f.id)} style={{ '--c1': f.c1, '--c2': f.c2, '--i': i }}>
              {b.boost > 1 && !res && <span className="qs-boost">🔥 ×{fmtPts(b.boost)}</span>}
              <span className="qs-maxpts">{f.max}</span>
              <FormatLogo f={f} small />
              {b.set && b.set.revealed && !res && <span className="qs-hintchip">{b.set.hint || b.set.theme}</span>}
              {res && <span className="qs-played">{res.points} pts</span>}
              {!b.set && !res && <span className="qs-empty">Pas de contenu</span>}
            </button>
          );
        })}
      </div>
      <div className="qs-jokers">
        <div className={`qs-joker${game.jokers.double ? ' is-used' : ''}`} title="À activer juste avant de lancer une épreuve"><span className="qs-joker-ico">💪</span><b>Double Dose</b><small>{game.jokers.double ? 'Utilisé' : '×2 sur l’épreuve de ton choix'}</small></div>
        <button type="button" className={`qs-joker${game.jokers.roulette ? ' is-used' : ''}`} disabled={game.jokers.roulette || spin} onClick={onRoulette}><span className="qs-joker-ico">🎰</span><b>Roulette</b><small>{game.jokers.roulette ? 'Utilisé' : 'Épreuve au hasard, points ×2'}</small></button>
        <button type="button" className={`qs-joker${game.jokers.hint ? ' is-used' : ''}${armed === 'radar' ? ' is-armed' : ''}`} disabled={game.jokers.hint} onClick={() => setArmed(armed === 'radar' ? '' : 'radar')}><span className="qs-joker-ico">📡</span><b>Radar</b><small>{game.jokers.hint ? 'Utilisé' : armed === 'radar' ? 'Choisis une épreuve à sonder' : 'Révèle l’indice d’une épreuve'}</small></button>
      </div>
      {armed === 'radar' && <p className="qs-armed-tip">Radar activé : clique sur l’épreuve dont tu veux connaître l’indice. Il restera visible pour les prochains candidats.</p>}
      {onRadar && null}
    </div>
  );
}

// Présentateur : il arrive, sa bulle s'écrit lettre par lettre, la voix off se lance
export const hostFor = (fid, cfg) => {
  const d = HOSTS[fid] || {}; const c = (cfg && cfg[fid]) || {};
  return { host: c.host || d.host, name: c.name || d.name, line: c.line || d.line, voice: c.voice || '' };
};
function Presenter({ f, host }) {
  const [n, setN] = useState(0);
  const [talking, setTalking] = useState(true);
  const audio = useRef(null);
  const line = host.line || f.pitch;
  useEffect(() => {
    setN(0); setTalking(true);
    let k = 0;
    const id = setInterval(() => { k += 1; setN(k); if (k % 3 === 0) sfx('blip'); if (k >= line.length) { clearInterval(id); if (!host.voice) setTalking(false); } }, 26);
    if (host.voice) {
      const a = new Audio(media(host.voice)); audio.current = a;
      a.onended = () => setTalking(false);
      a.play().catch(() => setTalking(false));
    }
    return () => { clearInterval(id); if (audio.current) { audio.current.pause(); audio.current = null; } };
  }, [line, host.voice]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = n >= line.length;
  const replay = () => { if (audio.current) { audio.current.currentTime = 0; audio.current.play().catch(() => {}); setTalking(true); } };
  return (
    <div className="qs-host-stage">
      <div className={`qs-host-who${talking ? ' is-talking' : ''}`}>
        <div className="qs-host-glow" />
        <Portrait v={host.host} name={host.name} />
        <span className="qs-host-name">{host.name}</span>
      </div>
      <div className="qs-bubble" onClick={() => setN(line.length)} role="presentation">
        <p>{line.slice(0, n)}<span className="qs-caret" style={{ opacity: done ? 0 : 1 }}>▍</span></p>
        <div className="qs-bubble-ctl">
          {!done && <button type="button" onClick={(e) => { e.stopPropagation(); setN(line.length); }}>Afficher tout ▸</button>}
          {host.voice && <button type="button" onClick={(e) => { e.stopPropagation(); replay(); }}>🔊 Réécouter</button>}
        </div>
      </div>
    </div>
  );
}

function Intro({ f, info, joker, canDouble, onDouble, onGo, onBack, host }) {
  const mult = (joker ? 2 : 1) * (info.boost || 1);
  return (
    <div className="qs-intro has-host" style={{ '--c1': f.c1, '--c2': f.c2 }}>
      <div className="qs-intro-logo"><div className="qs-burst" /><FormatLogo f={f} /></div>
      <Presenter f={f} host={host} />
      <ul className="qs-rules">{f.rules.map((r, n) => <li key={r} style={{ '--n': n }}>{r}</li>)}</ul>
      <div className="qs-intro-stats">
        <div><small>Points max</small><b>{f.max}</b></div>
        {info.boost > 1 && <div className="is-boost"><small>Boost épreuve boudée</small><b>×{fmtPts(info.boost)}</b></div>}
        {joker && <div className="is-joker"><small>{joker === 'roulette' ? 'Roulette' : 'Double Dose'}</small><b>×2</b></div>}
        <div className="is-total"><small>Jusqu’à</small><b>{Math.round(f.max * mult)}</b></div>
      </div>
      {info.set && (info.set.theme || info.set.revealed) && <div className="qs-theme"><small>Thème</small><b>{info.set.theme || info.set.hint}</b></div>}
      <div className="qs-intro-actions">
        {joker !== 'roulette' && <button type="button" className="qs-btn is-ghost" onClick={onBack}>← Changer d’épreuve</button>}
        {canDouble && joker !== 'roulette' && <button type="button" className={`qs-btn${joker === 'double' ? ' is-armed' : ''}`} onClick={onDouble}>💪 {joker === 'double' ? 'Double Dose activée' : 'Activer Double Dose'}</button>}
        <button type="button" className="qs-btn is-gold is-big" onClick={onGo}>Lancer l’épreuve</button>
      </div>
    </div>
  );
}

function Result({ f, r, onNext, last }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let n = 0; const id = setInterval(() => { n += Math.max(1, Math.ceil(r.points / 25)); setShown(Math.min(n, r.points)); if (n >= r.points) clearInterval(id); }, 40);
    sfx(r.points > 0 ? 'win' : 'bad');
    return () => clearInterval(id);
  }, [r.points]);
  return (
    <div className="qs-result" style={{ '--c1': f.c1, '--c2': f.c2 }}>
      <div className="qs-burst" />
      <FormatLogo f={f} small />
      <div className="qs-calc">
        <span><small>Épreuve</small><b>{r.base}</b></span>
        {r.joker && <span className="is-joker"><small>{r.joker === 'roulette' ? 'Roulette' : 'Double Dose'}</small><b>×2</b></span>}
        {r.boost > 1 && <span className="is-boost"><small>Boost</small><b>×{fmtPts(r.boost)}</b></span>}
      </div>
      <div className="qs-bigpts"><b>{shown}</b><small>points</small></div>
      <button type="button" className="qs-btn is-gold is-big" onClick={onNext}>{last ? 'Voir le score final' : 'Retour au plateau'}</button>
    </div>
  );
}

function Final({ game, api, onRanking, onLobby }) {
  const [rank, setRank] = useState(null);
  useEffect(() => {
    sfx('fanfare');
    api.get('/games').then(({ leaderboard }) => { const k = leaderboard.findIndex((g) => g._id === game._id); setRank({ k: k + 1, of: leaderboard.length, list: leaderboard.slice(0, 5) }); }).catch(() => {});
  }, [api, game._id]);
  return (
    <div className="qs-final">
      <div className="qs-confetti">{Array.from({ length: 40 }, (_, k) => <i key={k} style={{ '--x': `${(k * 53) % 100}%`, '--d': `${(k % 7) * 0.35}s`, '--h': (k * 47) % 360 }} />)}</div>
      <Avatar src={game.avatar} name={game.player} size={110} />
      <h2>{game.player}</h2>
      <div className="qs-bigpts"><b>{game.total}</b><small>points</small></div>
      {rank && <p className="qs-final-rank">{rank.k === 1 ? '👑 Nouveau numéro 1 du classement !' : `${rank.k}${rank.k === 1 ? 'er' : 'e'} sur ${rank.of} candidats`}</p>}
      <div className="qs-final-rounds">
        {game.rounds.map((r) => { const f = BY_ID[r.format]; return <div key={r.format} style={{ '--c1': f.c1, '--c2': f.c2 }}><FormatLogo f={f} small /><b>{r.points} pts</b><small>{r.base}/{f.max}{r.joker ? ' · ×2' : ''}{r.boost > 1 ? ` · ×${fmtPts(r.boost)}` : ''}</small></div>; })}
      </div>
      <div className="qs-intro-actions"><button type="button" className="qs-btn" onClick={onLobby}>Accueil</button><button type="button" className="qs-btn is-gold is-big" onClick={onRanking}>🏆 Classement général</button></div>
    </div>
  );
}

export default function QuizShow({ onClose }) {
  const isRegie = typeof window !== 'undefined' && window.location.hash.endsWith('-regie');
  const [key, setKey] = useState(() => read(KEY_STORE) || '');
  const api = useMemo(() => makeApi(key), [key]);
  const [view, setView] = useState('lobby');
  const [game, setGame] = useState(null);
  const [board, setBoard] = useState({});
  const [cfg, setCfg] = useState({});
  const [live, setLive] = useState([]);
  const [pending, setPending] = useState(null); // { format, joker }
  const [lastResult, setLastResult] = useState(null);
  const [armed, setArmed] = useState('');
  const [spin, setSpin] = useState('');
  const [cheatOpen, setCheatOpen] = useState(false);
  const [cheatLines, setCheatLines] = useState([]);
  const [err, setErr] = useState('');
  const [editorFormat, setEditorFormat] = useState(null);
  const bc = useRef(null);
  const state = useRef({});

  useEffect(() => {
    document.title = `${SHOW_NAME.join(' ')} · Bully’s Lair`;
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
    meta.content = 'noindex, nofollow';
  }, []);

  // Régie : diffuser l'état courant
  const broadcast = useCallback(() => {
    if (!bc.current) return;
    const s = state.current;
    bc.current.postMessage({ title: s.title, lines: s.lines, player: s.player, total: s.total });
  }, []);
  useEffect(() => {
    if (isRegie) return undefined;
    bc.current = channel();
    if (bc.current) bc.current.onmessage = (e) => { if (e.data && e.data.ask) broadcast(); };
    return () => { if (bc.current) bc.current.close(); };
  }, [isRegie, broadcast]);
  const cheat = useCallback((lines) => {
    setCheatLines(lines);
    state.current.lines = lines;
    broadcast();
  }, [broadcast]);

  useEffect(() => {
    const k = (e) => { if (/INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return; if (e.key === 'r' || e.key === 'R') setCheatOpen((o) => !o); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, []);

  const loadBoard = useCallback(async () => {
    try { setBoard((await api.get('/board')).formats); } catch (e) { setErr(e.message); }
    try { setCfg((await api.get('/config')).formats || {}); } catch (e) { /* réglages par défaut */ }
  }, [api]);
  const loadLive = useCallback(async () => { try { setLive((await api.get('/games')).live); } catch (e) { if (/Clé invalide/.test(e.message)) { write(KEY_STORE, null); setKey(''); } else setErr(e.message); } }, [api]);
  useEffect(() => { if (key && !isRegie) { loadLive(); loadBoard(); } }, [key, isRegie, loadLive, loadBoard]);

  useEffect(() => {
    const total = game ? game.rounds.reduce((s, r) => s + r.points, 0) : 0;
    const f = pending && BY_ID[pending.format];
    state.current = { ...state.current, player: game?.player, total, title: view === 'round' && f ? f.name.join(' ') : view === 'board' ? 'Plateau' : view === 'intro' && f ? `Présentation : ${f.name.join(' ')}` : 'Hors épreuve' };
    if (view !== 'round') state.current.lines = view === 'intro' && pending && board[pending.format]?.set ? [`Thème : ${board[pending.format].set.theme || '—'}`] : [];
    broadcast();
  }, [view, game, pending, board, broadcast]);

  if (isRegie) return <Regie />;
  if (!key) return <div className="qs-root"><Gate onKey={setKey} /></div>;

  const saveGame = async (g, extra = {}) => {
    setGame(g); write(GAME_STORE, g._id);
    try { const { game: saved } = await api.put(`/games/${g._id}`, { rounds: g.rounds, jokers: g.jokers, ...extra }); return saved; } catch (e) { setErr(`Sauvegarde impossible : ${e.message}`); return g; }
  };

  const pick = async (fid) => {
    if (armed === 'radar') {
      const set = board[fid]?.set; if (!set) return;
      sfx('joker');
      await api.post(`/sets/${set._id}/reveal`);
      setArmed('');
      await saveGame({ ...game, jokers: { ...game.jokers, hint: true } });
      loadBoard();
      return;
    }
    sfx('flip');
    setPending({ format: fid, joker: '' }); setView('intro');
  };

  const roulette = () => {
    const played = new Set(game.rounds.map((r) => r.format));
    const pool = FORMATS.filter((f) => !played.has(f.id) && board[f.id]?.set).map((f) => f.id);
    if (!pool.length) return;
    sfx('joker');
    const target = pool[Math.floor(Math.random() * pool.length)];
    const order = FORMATS.map((f) => f.id).filter((id) => pool.includes(id));
    const steps = 22 + order.indexOf(target) + order.length * 1;
    let k = 0;
    const step = () => {
      setSpin(order[k % order.length]); sfx('roll'); k += 1;
      if (k <= steps) setTimeout(step, 50 + k * k * 0.45);
      else {
        setSpin(target); sfx('win');
        setTimeout(() => { setSpin(''); setGame((g) => ({ ...g, jokers: { ...g.jokers, roulette: true } })); setPending({ format: target, joker: 'roulette' }); setView('intro'); }, 900);
      }
    };
    step();
  };

  const finishRound = async (base) => {
    const f = BY_ID[pending.format];
    const info = board[pending.format] || {};
    const boost = info.boost || 1;
    const mult = (pending.joker ? 2 : 1) * boost;
    const r = { format: f.id, setId: info.set?._id || '', theme: info.set?.theme || '', base, max: f.max, joker: pending.joker, boost, points: Math.round(base * mult) };
    const jokers = { ...game.jokers };
    if (pending.joker === 'double') jokers.double = true;
    if (pending.joker === 'roulette') jokers.roulette = true;
    const g = { ...game, jokers, rounds: [...game.rounds, r] };
    g.total = g.rounds.reduce((s, x) => s + x.points, 0);
    const done = g.rounds.length >= ROUNDS_PER_GAME;
    const saved = await saveGame(g, done ? { status: 'done' } : {});
    setGame({ ...g, ...saved, rounds: g.rounds });
    setLastResult(r); setView('result'); setCheatLines([]);
    if (done) write(GAME_STORE, null);
  };

  // Fermer une partie en cours sans l'ajouter au classement (les épreuves restent jouables)
  const closeGame = async (g) => {
    if (!window.confirm(`Fermer la partie de ${g.player} ? Elle ne comptera pas dans le classement et ses épreuves restent jouables.`)) return;
    try { await api.put(`/games/${g._id}`, { status: 'void' }); } catch (e) { setErr(e.message); return; }
    if (game && game._id === g._id) { setGame(null); setPending(null); write(GAME_STORE, null); setView('lobby'); }
    loadLive();
  };

  const Round = pending && ROUNDS[pending.format];
  const exit = () => { if (onClose) onClose(); };

  return (
    <div className={`qs-root v-${view}`}>
      <StageBg />
      {err && <div className="qs-toast" onClick={() => setErr('')} role="presentation">{err}</div>}

      <div className="qs-view" key={view === 'round' ? `round-${pending?.format}` : view}>
      {view === 'lobby' && (
        <>
          <button type="button" className="qs-exit" onClick={exit}>✕</button>
          <Lobby api={api} live={live}
            onStart={(g) => { setGame({ ...g, rounds: [], jokers: { double: false, roulette: false, hint: false } }); loadBoard(); setView('board'); sfx('start'); }}
            onResume={(g) => { setGame(g); loadBoard(); setView('board'); }}
            onEditor={() => { setEditorFormat(null); setView('editor'); }} onRanking={() => setView('ranking')} onCloseGame={closeGame} />
        </>
      )}
      {view === 'editor' && <QuizShowEditor api={api} initialFormat={editorFormat} onBack={() => { loadBoard(); setView(game && game.rounds.length < ROUNDS_PER_GAME && game.status !== 'done' ? 'board' : 'lobby'); }} />}
      {view === 'ranking' && <Ranking api={api} highlight={game?._id} onBack={() => setView('lobby')} />}

      {view === 'board' && game && (
        <>
          <div className="qs-hostbar">
            <button type="button" className="qs-btn is-ghost is-small" onClick={() => { setView('lobby'); loadLive(); }}>← Accueil</button>
            <button type="button" className="qs-btn is-ghost is-small" onClick={() => { setEditorFormat(null); setView('editor'); }}>✏️ Éditeur</button>
            <button type="button" className="qs-btn is-ghost is-small" onClick={() => closeGame(game)}>✕ Fermer la partie</button>
            <button type="button" className="qs-btn is-ghost is-small" onClick={() => window.open(`${window.location.pathname}${QS_HASH}-regie`, 'qs-regie', 'width=520,height=720')}>🎧 Régie</button>
          </div>
          <Board game={game} board={board} onPick={pick} onRoulette={roulette} armed={armed} setArmed={setArmed} spin={spin} />
        </>
      )}

      {view === 'intro' && pending && (
        <Intro f={BY_ID[pending.format]} info={board[pending.format] || {}} joker={pending.joker} canDouble={!game.jokers.double} host={hostFor(pending.format, cfg)}
          onDouble={() => { sfx('joker'); setPending({ ...pending, joker: pending.joker === 'double' ? '' : 'double' }); }}
          onBack={() => { setPending(null); setView('board'); }}
          onGo={() => { sfx('start'); setView('round'); }} />
      )}

      {view === 'round' && Round && (
        <div className="qs-stage" style={{ '--c1': BY_ID[pending.format].c1, '--c2': BY_ID[pending.format].c2 }}>
          <div className="qs-stage-top">
            <FormatLogo f={BY_ID[pending.format]} small />
            <div className="qs-stage-who"><Avatar src={game.avatar} name={game.player} size={38} /><b>{game.player}</b><span>{game.rounds.reduce((s, r) => s + r.points, 0)} pts</span></div>
            {(pending.joker || (board[pending.format]?.boost || 1) > 1) && <div className="qs-stage-mult">{pending.joker && <span>×2</span>}{(board[pending.format]?.boost || 1) > 1 && <span>🔥×{fmtPts(board[pending.format].boost)}</span>}</div>}
            <button type="button" className="qs-btn is-ghost is-small" onClick={() => { if (window.confirm('Abandonner l’épreuve en cours (0 point, l’épreuve reste jouable) ?')) { setView('board'); setPending(null); } }}>Annuler</button>
          </div>
          <Round key={pending.format} data={board[pending.format]?.set?.data || {}} onDone={finishRound} cheat={cheat} sfx={sfx} />
        </div>
      )}

      {view === 'result' && lastResult && (
        <Result f={BY_ID[lastResult.format]} r={lastResult} last={game.rounds.length >= ROUNDS_PER_GAME}
          onNext={() => { setPending(null); if (game.rounds.length >= ROUNDS_PER_GAME) setView('final'); else { loadBoard(); setView('board'); } }} />
      )}

      {view === 'final' && game && <Final game={game} api={api} onLobby={() => { setGame(null); setView('lobby'); loadLive(); loadBoard(); }} onRanking={() => setView('ranking')} />}

      </div>

      {cheatOpen && (
        <aside className="qs-cheat">
          <header><b>Antisèche</b><small>R pour cacher</small></header>
          {cheatLines.filter(Boolean).length ? <ul>{cheatLines.filter(Boolean).map((l, k) => <li key={k}>{l}</li>)}</ul> : <p>Rien pour l’instant.</p>}
        </aside>
      )}
    </div>
  );
}
